import "twin.macro";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import { DemandPlanVariant } from "@models/DemandPlanVariant";
import { ControlledTextInput, intValidation } from "@utils/forms";

import { ColumnTooltip } from "../columns/DemandPlanVariantTooltips";
import { EditingContext } from "./EditingContext";

type EditableCellProps = {
  name: string;
  rec: DemandPlanVariant;
  tooltipTitle: string | JSX.Element;
  onSubmit: (value: string) => void;
  children: string | JSX.Element | JSX.Element[];
  cellClassName?: string;
};

export const EditableCell = ({
  name,
  rec,
  onSubmit,
  tooltipTitle,
  children,
  cellClassName,
}: EditableCellProps) => {
  const { getValues } = useFormContext();
  const fieldName = `${name}-${rec.id}`;
  const editor = useContext(EditingContext);
  const current = editor.current;
  const [editing, setEditing] = useState(current?.id === rec.id);

  const handleSubmit = useCallback(
    (v) => {
      onSubmit(v);
      editor.complete(rec);
    },
    [onSubmit, editor, rec]
  );

  // Listen to the editor to see if the context has changed to handle submit
  useEffect(() => {
    const wasEditing = editing;
    const nowEditing = current?.id === rec.id;
    setEditing(nowEditing);

    if (!nowEditing && wasEditing) {
      const value = getValues(fieldName);
      handleSubmit(value);
    }
  }, [current, rec, fieldName, editing, setEditing, getValues, handleSubmit]);

  return useMemo(() => {
    return (
      <>
        {/* If curently editing */}
        {editing ? (
          <div tw="flex justify-end">
            <CellEditorInput name={fieldName} onSubmit={handleSubmit} />
          </div>
        ) : (
          // If not editing, display the value in the cell
          <ColumnTooltip underline={false} title={tooltipTitle}>
            <span
              onClick={() => {
                // Start Cell Editing
                editor.canEdit(rec) && editor.start(rec);
              }}
              className={`group ${editor.canEdit(rec) ? "cursor-pointer" : ""}`}
              tw="text-right block cursor-default min-w-[80px] font-bold py-[18px]"
            >
              <span
                className={`${cellClassName} ${editor.canEdit(rec) ? "group-hover:border-solid" : ""}`}
                tw="border-b border-dashed border-spacing-4 border-neutral-400"
              >
                {children}
              </span>
            </span>
          </ColumnTooltip>
        )}
      </>
    );
  }, [
    editing,
    handleSubmit,
    cellClassName,
    children,
    editor,
    fieldName,
    rec,
    tooltipTitle,
  ]);
};

//TODO type inputs
const CellEditorInput = ({ name, onSubmit, ...inputProps }) => {
  const { control, getValues } = useFormContext();

  return (
    <ControlledTextInput
      name={name}
      control={control}
      autoFocus
      tw="relative top-0 w-[80px] my-[6px]"
      size="small"
      inputProps={{
        style: { textAlign: "right" },
      }}
      rules={intValidation}
      onBlur={(_e) => {
        onSubmit(getValues(name));
      }}
      onFocus={(e) => {
        e.target.select();
      }}
      {...inputProps}
    />
  );
};
